import { AppState } from 'Store'
import { convertObjectToShortData, OkfsModel } from 'Modules'
import { ShortDataModel, ProductAquiringEquipModel } from 'Models'
import { NotificationChannelModel } from 'Pages/admin'
import { createSelector } from 'reselect'
import { BaseItem, BaseItemEntries } from 'Types/item'

const getAll = (state: AppState) => state.modules.dicts.all

const getIncorporationForm = (state): ShortDataModel[] =>
	state.modules.dicts.all?.incorporation_form ?? []

const getRbidosTypes = (state): ShortDataModel[] =>
	state.modules.dicts.all?.product.rbidos_types ?? []

const getNotificationTemplateGroups = (state): ShortDataModel[] =>
	state.modules.dicts.all?.notification_template_groups ?? []

const getNotificationChannelsAll = (state): NotificationChannelModel[] =>
	state.modules.dicts.all?.notification_channels ?? []

const getEventTypesPartner = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.partner ?? []

const getEventTypesUser = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.user ?? []

const getEventTypesOrder = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.order ?? []

const getEventTypesRbidosRequest = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.log ?? []

const getEventTypesSmsRule = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.sms_rule ?? []

const getEventTypesReport = (state): ShortDataModel[] =>
	(state.modules.dicts.all?.type_of_reports ?? []).map(
		(item): ShortDataModel => convertObjectToShortData(item),
		ShortDataModel
	)

const getEventTypesKnowledge = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.knowledge ?? []

const getAcquiringEquip = (state): ProductAquiringEquipModel[] =>
	state.modules.dicts.all?.acquiring_equip ?? []

const getEventTypesCustomerIdentification = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.customer_identification ?? []

const getTypeRbidosLead = (state): ShortDataModel[] =>
	state.modules.dicts.all?.rbidos_request?.lead_types ?? []

const getTerritorialBanks = (state): ShortDataModel[] =>
	state.modules.dicts.all?.territorial_bank ?? []

const getEventTypesStatus = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.status_upload ?? []
const getStatusTypes = (state): ShortDataModel[] =>
	state.modules.dicts.all?.status_upload_types ?? []
const getTariffsDict = (state): ShortDataModel =>
	state.modules.dicts.all?.tariff_product_mapping ?? []

const getOnlinePrefilledFields = (state): string[] =>
	state.modules.dicts.all?.rbidos_online_prefilled_fields ?? []

const getTaxationForm = (state): ShortDataModel[] => state.modules.dicts.all?.taxation_form ?? []

const getFreeTaxBases = (state): ShortDataModel[] => state.modules.dicts.all?.free_tax_bases ?? []

const getEventTypesBg = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.bg_event ?? []
const getEventTypesBgAst = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.bg_ast ?? []
const getPartnersDigitalConfirmations = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partners_digital_confirmations ?? []

const getEventTypesSberCrm = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.sbercrm ?? []
const getLeadStatuses = (state): ShortDataModel[] => state.modules.dicts.all?.lead?.states ?? []
const getLeadParentTypes = (state): ShortDataModel[] =>
	state.modules.dicts.all?.lead?.parent_types ?? []
const getLeadBearer = (state): ShortDataModel[] => state.modules.dicts.all?.lead?.bearers ?? []
const getDepartmentKinds = (state): ShortDataModel[] =>
	state.modules.dicts.all?.department_kinds ?? []
const getOnlineOrdersKind = (state): Omit<ShortDataModel, 'id'>[] =>
	state.modules.dicts.all?.paperless_request_kinds ?? []

const getOnlineOrdersKindItems = createSelector(getOnlineOrdersKind, (kinds) =>
	kinds.map(
		(kind): ShortDataModel => ({
			...kind,
			id: kind.slug,
		})
	)
)
const getMerchants = (state): BaseItem[] => state.modules.dicts.all?.merchant ?? []
const getChannels = (state): BaseItem[] => state.modules.dicts.all?.channel ?? []

const getPackageOfServices = (state): ShortDataModel[] =>
	state.modules.dicts.all?.product?.package_of_services ?? []

const getReserveAccountStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.reserve_account_statuses ?? []
const getPartnerRequestKinds = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request?.kinds ?? []
const getPartnerRequestStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request?.statuses ?? []
const getPartnerRequestCheckings = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request?.checkings ?? []
const getPartnerRequestConsents = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request?.consents ?? []
const getPartnerRequestOrderAgreementStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request_order_agreement?.statuses ?? []

const getClientTypes = (state): BaseItemEntries =>
	state.modules.dicts.all?.product?.client_types ?? []

const getSettingIbro = (state): ShortDataModel[] => state.modules.dicts.all?.setting?.ibro ?? []

const getStaffInfotecsRequestAgentsAutoProcessingStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.staff_infotecs_request_agents_auto_processings?.statuses ?? []
const getStaffInfotecsTranslateStatuses = createSelector(
	getStaffInfotecsRequestAgentsAutoProcessingStatuses,
	(statuses) =>
		statuses.reduce((prev, next) => {
			prev[next.id] = next.name
			return prev
		}, {})
)

const getInterfaceElementsKinds = (state): ShortDataModel[] =>
	state.modules.dicts.all?.interface_elements?.kinds ?? []

const getleadFilter = (state): ShortDataModel[] =>
	state.modules.dicts.all?.lead?.order_statuses ?? []

const getOkfs = (state): OkfsModel[] => state.modules.dicts.all?.okfs ?? []

const getEio = (state) => state.modules.dicts.all?.rbidos_request?.eio_marks ?? []

const getPaymentPurposeContracts = (state) =>
	state.modules.dicts.all?.rbidos_request?.payment_purpose_contracts ?? []

const getOkfsCode = createSelector(getOkfs, (okfss) =>
	okfss.map(
		(okfs): ShortDataModel => ({
			...okfs,
			id: okfs.code,
			name: `${okfs.code} (${okfs.description})`,
		})
	)
)

const getStagesDrsFakeResponsesFilter = (state): ShortDataModel[] =>
	state.modules.dicts.all?.fake_responses?.reserve_accounts?.stages ?? []

const getPartnerRequestCategories = (state): ShortDataModel[] =>
	state.modules.dicts.all?.partner_request?.categories ?? []

const getLandingStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.landing_statuses ?? []

const getOptionsPaymentStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.order_payment_statuses ?? []

const getOptionsCrmStatuses = (state): ShortDataModel[] =>
	state.modules.dicts.all?.order_crm_statuses ?? []

const getBgEventNotification = (state): ShortDataModel[] =>
	state.modules.dicts.all?.setting?.bg_event_notification ?? []
const getEventTypesDepartment = (state): ShortDataModel[] =>
	state.modules.dicts.all?.event_types?.department ?? []
const getTimeOffUserStatus = (state): ShortDataModel[] =>
	state.modules.dicts.all?.time_off?.user_statuses ?? []
const getTimeOffKind = (state): ShortDataModel[] => state.modules.dicts.all?.time_off?.kinds ?? []
const getNameReserveAccountStatuses = createSelector(
	getReserveAccountStatuses,
	(_, key) => key,
	(accountStatuses, key) => (accountStatuses ?? []).find((status) => status.id === key)?.name
)

export const dictsSelector = {
	getAll,
	getTaxationForm,
	getFreeTaxBases,
	getAcquiringEquip,
	getPartnersDigitalConfirmations,
	getLeadStatuses,
	getLeadParentTypes,
	getPackageOfServices,
	getReserveAccountStatuses,
	getDepartmentKinds,
	getOnlineOrdersKindItems,
	getNotificationTemplateGroups,
	getNotificationChannelsAll,
	getPartnerRequestKinds,
	getPartnerRequestStatuses,
	getPartnerRequestCheckings,
	getPartnerRequestConsents,
	getPartnerRequestOrderAgreementStatuses,
	getClientTypes,
	getSettingIbro,
	getStaffInfotecsTranslateStatuses,
	getMerchants,
	getChannels,
	getTerritorialBanks,
	getIncorporationForm,
	getInterfaceElementsKinds,
	getleadFilter,
	getOkfs,
	getPaymentPurposeContracts,
	getOkfsCode,
	getTariffsDict,
	getEio,
	getStagesDrsFakeResponsesFilter,
	getPartnerRequestCategories,
	getEventTypesCustomerIdentification,
	getLandingStatuses,
	getRbidosTypes,
	getEventTypesPartner,
	getEventTypesUser,
	getEventTypesOrder,
	getEventTypesRbidosRequest,
	getEventTypesSmsRule,
	getEventTypesReport,
	getEventTypesKnowledge,
	getTypeRbidosLead,
	getEventTypesStatus,
	getStatusTypes,
	getOnlinePrefilledFields,
	getEventTypesBg,
	getEventTypesBgAst,
	getEventTypesSberCrm,
	getLeadBearer,
	getOptionsPaymentStatuses,
	getOptionsCrmStatuses,
	getBgEventNotification,
	getEventTypesDepartment,
	getTimeOffUserStatus,
	getTimeOffKind,
	getNameReserveAccountStatuses,
}
